import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import StarIcon from '@material-ui/icons/Star';
import NotesIcon from '@material-ui/icons/Notes';

import {
  GenericStatus,
  LeaderboardConfigFragment,
  LeaderboardConfigInput,
  UserSegmentFragment,
} from 'api';
import { Loaders, Panel, ValidatedSwitch, ValidatedTextField } from 'components';

import EditablePointDialog from './EditablePointDialog';
import LeaderboardView from './View';
import LeaderboardEdit from './Edit';

interface IProps {
  config: LeaderboardConfigFragment;
  segments: UserSegmentFragment[];
  working: boolean;
}

const useStyles = makeStyles(theme => ({
  dividerHeader: {
    margin: '12px -16px 24px -16px',
  },
  pointsButton: {
    marginRight: theme.spacing(2),
  },
  saveButton: {
    marginTop: theme.spacing(4),
    minWidth: 150,
  },
}));

const Leaderboard: FC<IProps> = ({ config, working, segments }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const formik = useFormikContext<LeaderboardConfigInput>();
  const classes = useStyles();

  const { errors, isValid, setFieldValue, submitForm } = formik;
  const { leaderboard_status, segment_ids } = formik.values;
  const { all_users } = config;

  const isActive = leaderboard_status === GenericStatus.Active;

  function checkedFn(value: GenericStatus) {
    return value === GenericStatus.Active;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>, value: boolean) {
    const status = value ? GenericStatus.Active : GenericStatus.Disabled;
    setFieldValue('leaderboard_status', status);

    setTimeout(() => {
      void submitForm();
    });
  }

  function renderToggle() {
    const hasSegments = !all_users && !segment_ids.length;
    const hasErrors = !!Object.keys(errors).length || hasSegments;

    return (
      <ValidatedSwitch
        name="leaderboard_status"
        color="primary"
        label={t('common:status')}
        disabled={hasErrors}
        onChange={handleChange}
        checkedFn={checkedFn}
      />
    );
  }

  function renderEditBody() {
    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <LeaderboardEdit config={config} />
      </Fragment>
    );
  }

  function renderActions() {
    return (
      <Fragment>
        <EditablePointDialog config={config} open={open} onClose={() => setOpen(false)} />
        <Button
          className={classes.pointsButton}
          onClick={() => setOpen(true)}
          endIcon={<NotesIcon />}
        >
          {t('leaderboard:editPoints')}
        </Button>
      </Fragment>
    );
  }

  function renderHeader(editing: boolean) {
    const readTitle = <Typography variant="subtitle2">{config.title}</Typography>;
    const editTitle = (
      <ValidatedTextField
        name="title"
        i18nKey="admin:leaderboardsPlaceholder"
        color="primary"
        TextFieldProps={{ margin: 'none', label: null }}
      />
    );

    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <StarIcon color={isActive ? 'primary' : 'disabled'} />
        </Grid>
        <Grid item xs>
          {editing ? editTitle : readTitle}
        </Grid>
      </Grid>
    );
  }

  function renderBody(editing: boolean) {
    return editing ? renderEditBody() : <LeaderboardView segments={segments} config={config} />;
  }

  function renderControls(editing: boolean, setEditing: (editing: boolean) => void) {
    return (
      <Grid container justify="flex-end" spacing={2}>
        <Grid item>
          <Button
            fullWidth
            variant="outlined"
            className={classes.saveButton}
            onClick={() => {
              formik.resetForm();
              setEditing(false);
            }}
          >
            {t('admin:doneEditing')}
          </Button>
        </Grid>
        <Grid item>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            className={classes.saveButton}
            disabled={!isValid}
          >
            {working ? <Loaders.Standard height={24} width={60} /> : t('common:save')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Panel
      header={renderHeader}
      actions={renderActions}
      body={renderBody}
      toggle={renderToggle}
      controls={renderControls}
    />
  );
};

export default Leaderboard;
